import { useEffect, useState, useRef } from 'react';
import axios from 'axios';

import { useAuth } from './hooks/use-auth';
import { getTraceContext } from '@/shared/utils/trace-context';
import { matchesPathPrefixes } from './utils';
import { isE2ETestMode } from './utils/e2e';
import { createTokenManager } from './utils/token-manager';
import { TOKEN_FETCH_ERROR_PREFIX } from '@/shared/utils/error-handler';

// Paths that receive the Auth0 bearer token. `/api/v1` is the main backend;
// `/appliance-support` is the appliance-only host control endpoint (single-VM),
// which is verified against the same Auth0 token on the host.
const AUTH_TOKEN_PATH_PREFIXES = ['/api/v1', '/appliance-support'];

/** Singleton token manager shared across all interceptor invocations */
const tokenManager = createTokenManager();

/**
 * Provides an Axios request interceptor that attaches Auth0 access tokens
 * to outgoing API requests.
 *
 * Features:
 * - Retries transient token fetch failures with exponential backoff
 * - Deduplicates concurrent token fetch requests
 * - Reports failures to Sentry with original error context
 * - Handles non-recoverable errors (e.g., missing refresh token) by logging out
 *
 * @param props - Component props
 * @param props.children - Child components rendered after interceptor is ready
 * @returns Children when interceptor is initialized, null while loading
 */
export function AxiosInterceptorProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const {
    getAccessTokenSilently,
    logout,
    isAuthenticated,
    isLoading: isAuthLoading,
  } = useAuth();
  const [isReady, setIsReady] = useState(false);
  // use ref to store the interceptor id
  const interceptorIdRef = useRef<number | undefined>(undefined);

  // use refs to store latest auth values to avoid stale closures in interceptor
  const isAuthenticatedRef = useRef(isAuthenticated);
  const getAccessTokenSilentlyRef = useRef(getAccessTokenSilently);
  const logoutRef = useRef(logout);

  // Update refs whenever values change
  // eslint-disable-next-line no-restricted-syntax -- reason: Update refs to avoid stale closures in axios interceptors
  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
    getAccessTokenSilentlyRef.current = getAccessTokenSilently;
    logoutRef.current = logout;
  }, [isAuthenticated, getAccessTokenSilently, logout]);

  // eslint-disable-next-line no-restricted-syntax -- reason: Setup axios interceptor requires imperative API initialization
  useEffect(() => {
    // If interceptor already exists (React StrictMode double-render), skip
    if (interceptorIdRef.current !== undefined) {
      setIsReady(true);
      return;
    }

    const setupInterceptor = () => {
      // Check for E2E test mode (SSR-safe: checks build-time VITE_E2E_TEST_MODE first,
      // then runtime window.__ENV__ if available)
      const isE2EMode = isE2ETestMode();

      // Setup interceptor - read from refs to avoid stale closure
      const requestInterceptor = axios.interceptors.request.use(
        async config => {
          // E2E test mode - add test header and skip auth
          if (isE2EMode) {
            config.headers['X-E2E-Test-Mode'] = 'true';
            return config;
          }

          // Read latest values from refs instead of closure
          const currentIsAuthenticated = isAuthenticatedRef.current;
          const currentGetAccessTokenSilently =
            getAccessTokenSilentlyRef.current;
          const currentLogout = logoutRef.current;

          // Production mode - add request ID header and auth token for API requests
          const traceContext = getTraceContext();
          config.headers['x-kog-request-id'] = traceContext.requestId;

          // REQUIRED: Session affinity header for multi-pod Astral deployments.
          // Envoy consistent-hash LB uses this to pin a run to one pod.
          // See: astral/helm/astral/templates/session-affinity-policy.yaml
          const runIdMatch = config.url?.match(/\/runs\/([0-9a-zA-Z_-]+)/);
          if (runIdMatch) {
            config.headers['x-astral-run-id'] = runIdMatch[1];
          }

          // Only try to get token if authenticated
          if (currentIsAuthenticated) {
            const result = await tokenManager.getToken(
              currentGetAccessTokenSilently,
            );

            if (result.success) {
              config.headers.Authorization = `Bearer ${result.token}`;
            } else {
              // Non-recoverable error - logout if needed
              if (result.requiresLogout) {
                currentLogout();
              }

              return Promise.reject(
                new Error(
                  `${TOKEN_FETCH_ERROR_PREFIX}: ${result.error.message}`,
                ),
              );
            }
          }

          return config;
        },
        error => Promise.reject(error),
        {
          runWhen: config =>
            matchesPathPrefixes(config, AUTH_TOKEN_PATH_PREFIXES),
        },
      );

      return requestInterceptor;
    };

    interceptorIdRef.current = setupInterceptor();
    setIsReady(true);
  }, []);

  if (isAuthLoading || !isReady) {
    return null;
  }

  return <>{children}</>;
}
